import { ColDef } from "@ag-grid-community/core";
import { Button } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getBanksListQueryKey,
  useBanksBulkUpsert,
} from "gen/endpoints/bank/bank";
import { useGlobalEntityListSuspense } from "gen/endpoints/global-entity/global-entity";
import type { BankUpsertData } from "gen/model/bankUpsertData";
import { get } from "lodash-es";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContainer from "shared/components/layout/PageContainer";
import PageContent from "shared/components/layout/PageContent";
import { RouteLink } from "shared/components/link/RouteLink";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { useBulkEditor } from "shared/components/table/maybern/useBulkEditor";
import { useToast } from "shared/components/toast/useToast";
import useDocumentTitle from "shared/hooks/useDocumentTitle";
import { rootRoute } from "shared/navigation/Routes";
import {
  flattenApiErrorData,
  getApiError,
  getApiErrorFromResponse,
} from "shared/utils/ErrorUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const EntitiesBulkAddBanksPage = () => {
  const { t } = useTranslation(["common", "entity"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast, showErrorToast } = useToast();

  useDocumentTitle(t("entity:bulkAddBanks.title"));

  const { data: entities } = useGlobalEntityListSuspense();

  const { mutateAsync, variables, error, reset } = useBanksBulkUpsert();

  const columnDefs = useMemo<ColDef<BankUpsertData>[]>(
    () => [
      {
        field: "entity_id",
        headerName: t("entity:bankInfo.entity"),
        type: ["dropdownEditable"],
        cellEditorParams: {
          title: t("entity:bankInfo.selectEntity"),
          options: entities.map((i) => ({
            label: i.legal_name,
            value: i.id,
          })),
        },
      },
      {
        field: "name",
        headerName: t("entity:bankInfo.bankName"),
        editable: true,
        type: ["textEditable"],
        cellEditorParams: {
          placeholder: t("entity:bulkAddBanks.bankNamePlaceholder"),
        },
      },
      {
        field: "aba",
        headerName: t("entity:bankInfo.abaRoutingNumber"),
        editable: true,
        type: ["textEditable"],
        cellEditorParams: {
          placeholder: t("entity:bulkAddBanks.abaRoutingNumberPlaceholder"),
        },
      },
      {
        field: "account_number",
        headerName: t("entity:bankInfo.accountNumber"),
        editable: true,
        type: ["textEditable"],
        cellEditorParams: {
          placeholder: t("entity:bulkAddBanks.accountNumberPlaceholder"),
        },
      },
      {
        field: "account_name",
        headerName: t("entity:bankInfo.accountName"),
        editable: true,
        type: ["textEditable"],
        cellEditorParams: {
          placeholder: t("entity:bulkAddBanks.accountNamePlaceholder"),
        },
      },
      {
        field: "for_further_credit_to",
        headerName: t("entity:bankInfo.furtherCreditTo"),
        editable: true,
        type: ["textEditable"],
        initialWidth: 220,
        cellEditorParams: {
          placeholder: t("entity:bulkAddBanks.furtherCreditToPlaceholder"),
        },
      },
      {
        field: "dda",
        headerName: t("entity:bankInfo.ddaNumber"),
        editable: true,
        type: ["textEditable"],
        cellEditorParams: {
          placeholder: t("entity:bulkAddBanks.ddaNumberPlaceholder"),
        },
      },
    ],
    [entities, t],
  );

  const { rowData, onClickClear, tableProps } = useBulkEditor<BankUpsertData>({
    columnDefs,
    controllerProps: {
      getSubmittedFieldValue: useCallback(
        (rowIndex, field) =>
          variables?.data.upsert_data_list[rowIndex]?.[
            field as keyof BankUpsertData
          ],
        [variables],
      ),
      getFieldError: useCallback(
        (rowIndex, field) =>
          get(
            getApiErrorFromResponse(error)?.data,
            `upsert_data_list.${rowIndex}.${field}`,
          ),
        [error],
      ),
    },
  });

  const onSubmit = async () => {
    try {
      const resp = await mutateAsync({ data: { upsert_data_list: rowData } });
      showToast({
        variant: "success",
        message: t("entity:bulkAddBanks.success", { count: resp.length }),
      });
      await invalidateQueryKeys(queryClient, getBanksListQueryKey());
      navigate(rootRoute({}).entities({}).$);
    } catch (e) {
      const rootListError = flattenApiErrorData(getApiError(e)?.data).find(
        (error) => error.fieldPath === "upsert_data_list",
      );
      if (rootListError) {
        showErrorToast(rootListError.error);
      }
    }
  };

  return (
    <PageContainer>
      <PageContent isFullHeight={true}>
        <PageBody>
          <TableBox isFullWidth={true} isBulkEditor={true}>
            <TableBoxHeader>
              <TableBoxTitle>{t("entity:bulkAddBanks.title")}</TableBoxTitle>
              <TableBoxActions showFullScreen={false}>
                <Button
                  size="xs"
                  colorScheme="secondary"
                  onClick={() => {
                    onClickClear();
                    reset();
                  }}
                >
                  {t("common:clear")}
                </Button>
              </TableBoxActions>
            </TableBoxHeader>
            <TableBoxBody>
              <MaybernTable {...tableProps} />
            </TableBoxBody>
          </TableBox>
          <FormFooter>
            <FormFooterActions>
              <Button
                colorScheme="secondary"
                as={RouteLink}
                to={rootRoute({}).entities({}).$}
              >
                {t("common:cancel")}
              </Button>
              <Button onClick={onSubmit}>
                {t("entity:bulkAddBanks.submit")}
              </Button>
            </FormFooterActions>
          </FormFooter>
        </PageBody>
      </PageContent>
    </PageContainer>
  );
};
